import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useState } from "react"
import {
  MonthlyExtractWindow,
  TriggerResultDto,
  useTriggerMonthlyExtractMutation,
} from "src/features/things/monthlyExtractApi"
import { fmtPeriod, WINDOW_LABEL } from "./monthlyReportsChrome"

const FIRST_YEAR = 2020

const errorText = (error: unknown): string => {
  const e = error as { data?: { message?: string }; status?: unknown }
  return e?.data?.message ?? `request failed (${String(e?.status ?? "network")})`
}

/**
 * LOCKER-469 DoD3 — pick ANY year-month up to the current one (N1) and start an
 * extract. A month that already has a run gets a new one beside it (N3): the
 * dialog says so rather than refusing.
 */
export const GenerateExtractDialog: FC<{
  open: boolean
  onClose: () => void
  onTriggered: (result: TriggerResultDto) => void
  /** Periods that already have at least one run, for the N3 notice. */
  existingPeriods: ReadonlySet<string>
}> = ({ open, onClose, onTriggered, existingPeriods }) => {
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1
  const prev = currentMonth === 1 ? { y: currentYear - 1, m: 12 } : { y: currentYear, m: currentMonth - 1 }

  const [year, setYear] = useState(prev.y)
  const [month, setMonth] = useState(prev.m)
  // LOCKER-496 DoD5 — `utc` is what every published report used.
  const [window, setWindow] = useState<MonthlyExtractWindow>("utc")
  const [trigger, { isLoading, error, reset }] = useTriggerMonthlyExtractMutation()

  const period = `${year}-${String(month).padStart(2, "0")}`
  const isFuture = year * 12 + month > currentYear * 12 + currentMonth
  const years = Array.from({ length: currentYear - FIRST_YEAR + 1 }, (_, i) => currentYear - i)

  const close = () => {
    reset()
    onClose()
  }

  const confirm = async () => {
    const res = await trigger({ year, month, window })
    if ("data" in res && res.data) {
      onTriggered(res.data.data)
      close()
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth data-ui="UI-SL2W-278">
      <DialogTitle>Generate a monthly extract</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            size="small"
            label="Year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            sx={{ minWidth: 110 }}
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Month"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            sx={{ minWidth: 170 }}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <MenuItem key={m} value={m} disabled={year === currentYear && m > currentMonth}>
                {fmtPeriod(`${year}-${String(m).padStart(2, "0")}`)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <TextField
          select
          fullWidth
          size="small"
          label="Window"
          value={window}
          onChange={(e) => setWindow(e.target.value as MonthlyExtractWindow)}
          sx={{ mt: 2 }}
          data-testid="generate-window"
        >
          {(Object.keys(WINDOW_LABEL) as MonthlyExtractWindow[]).map((w) => (
            <MenuItem key={w} value={w}>
              {w} — {WINDOW_LABEL[w]}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
          Runs the OLAP v4 reports for <strong>{fmtPeriod(period)}</strong> ({window} window):
          the GJC workbook and the all-sites occupation workbook. Archives both and posts to
          Slack; nothing is shared until you create a share link on the run page. It takes a
          few minutes; this page follows it.
        </Typography>
        {year === currentYear && month === currentMonth && (
          <Alert severity="info" sx={{ mt: 2 }}>
            The current month is still running — the extract covers it up to now.
          </Alert>
        )}
        {existingPeriods.has(period) && (
          <Alert severity="info" sx={{ mt: 2 }} data-testid="generate-existing-period">
            {fmtPeriod(period)} already has a run. A new run is added beside it; the earlier
            one and its share link are kept.
          </Alert>
        )}
        {!!error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorText(error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" onClick={() => void confirm()} disabled={isLoading || isFuture}>
          {isLoading ? "Starting…" : "Generate"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
